import {Component, OnInit} from '@angular/core';
import {PaymentStatusFetchBase} from "../../payments/payment-status-fetch-base";
import {LocationService} from "../../location.service";
import {Router} from "@angular/router";
import {LocalStorageService} from "../../../local-storage.service";
import {StoreOrderService} from "../../../stores/store-order/store-order.service";
import {StoreService} from "../store.service";
import {IntegrationPaymentService} from "../../payments/integration.payment.service";
import StoreUtils from "../utils/StoreUtils";
import {InactivityService} from "../../../shared/inactivity.service";
import {WindowRefService} from "../../../window.service";
import {WhitelabelService} from "../../../shared/whitelabel.service";
import {HideLoader, ShowLoader} from "../../../loader/+state/loader.actions";
import {Store} from "@ngrx/store";
import {getSelectedLang} from "../+state/stores.selectors";
import {takeUntil} from "rxjs/operators";
import {ViewStateUpdateUserLanguage} from "../+state/stores.actions";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: 'app-qr-payment',
  templateUrl: './qr-payment.component.html',
  styleUrl: './qr-payment.component.scss'
})
export class QrPaymentComponent extends PaymentStatusFetchBase implements OnInit {

  private fetchInterval: number = 500;
  private maxTimeout: number = 180000;
  private readonly PAYMENT_PAY_DETAILS = 'paymentPayDetails';
  protected storeId: number;
  protected qrCodePaymentUrl: string;
  protected paymentPayDetails: any;
  protected locale: string;
  selectedLang: string;
  lang: string;
  langLoaded = false;

  constructor(protected locationService: LocationService,
              protected router: Router,
              protected storageService: LocalStorageService,
              protected storeOrderService: StoreOrderService,
              protected storeService: StoreService,
              protected integrationPaymentService: IntegrationPaymentService,
              protected inactivityService: InactivityService,
              private windowRefService: WindowRefService,
              private whitelabelService: WhitelabelService,
              protected store: Store<any>,
              private translate: TranslateService
  ) {
    super(locationService, router, storageService, storeOrderService, storeService, store, integrationPaymentService);
  }


  ngOnInit(): void {
    this.paymentPayDetails = this.getPaymentPayDetailsFromLocalStorage();
    const selectedStore = this.paymentPayDetails.selectedStore;
    // in activity service
    this.inactivityService.setModalShown(true);
    if ( selectedStore.settings.INACTIVITY_OPTION ){
      this.inactivityService.setTimeout(selectedStore);
    }
    this.storeId = selectedStore.id;
    this.qrCodePaymentUrl = this.getQrCodePaymentUrl();
    this.locale = StoreUtils.getStoreLocale(selectedStore);

    this.store.select(getSelectedLang)
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe(value => {
      this.selectedLang = value;
    });

    this.loadLanguage(this.translate.getBrowserLang())
    .catch(_ => {
      this.loadLanguage('en');
      this.langLoaded = true;
    });

    super.onInit();

  }

  protected getPaymentDetails() {
    return {
      storeId: this.paymentPayDetails.selectedStore.id,
      orderId: this.paymentPayDetails.orderId,
      isStandalonePayment: this.paymentPayDetails.isStandalonePayment,
      redirectUrl: this.paymentPayDetails.redirectUrl,
      paymentTransactionId: this.paymentPayDetails?.paymentTransaction?.id,
      referenceType: this.paymentPayDetails?.paymentTransaction?.referenceType
    }
  }

  private getPaymentPayDetailsFromLocalStorage() {
    const paymentPayDetailsStr = this.storageService.getSavedState(this.PAYMENT_PAY_DETAILS);
    return JSON.parse(paymentPayDetailsStr);
  }

  protected getRedirectUrl(providerFromResponse: string): string {
    const { storeId, orderId, paymentTransactionId, redirectUrl, referenceType } = this.getPaymentDetails();
    let url = redirectUrl
    if (!redirectUrl) {
      url = window.location.origin + "/payment-redirect/" + providerFromResponse + "/" + storeId + "/" + paymentTransactionId + "/" + orderId + "?referenceType=" + referenceType;
    }
    return url;
  }

  protected onStopFetchPaymentStatus() {
    this.storageService.removeSavedState(this.PAYMENT_PAY_DETAILS);
  }

  getQrCodePaymentUrl() {
    const { aliasName, id: storeId } = this.paymentPayDetails.selectedStore;
    const { orderId, paymentTransaction } = this.paymentPayDetails;

    const protocol = this.windowRefService.nativeWindowLocation.protocol;
    const host = this.whitelabelService.getHost();
    const externalCustomDomain = this.paymentPayDetails?.selectedStore?.settings?.EXTERNAL_DOMAIN;

    const finalDomainUrl = externalCustomDomain
      ? (externalCustomDomain.startsWith('http://') || externalCustomDomain.startsWith('https://')
        ? externalCustomDomain
        : 'https://' + externalCustomDomain)
      : (protocol === 'httpsionic:' ? 'https:' : protocol) + '//' + aliasName + '.' + host;

    return `${finalDomainUrl}/payment-pay/${storeId}/${orderId}/${paymentTransaction.referenceType}/complete`;
  }



  onGoBack(event?) {
    if (event) event.preventDefault();
    if (this.paymentPayDetails?.isStandalonePayment) {
      this.handleStandalonePaymentBack();
    } else {
      this.handleOrderBack();
    }
  }

  private handleOrderBack() {
    let backUrl = '';
    if (this.paymentPayDetails?.backToCurrentCat) {
      backUrl = `category/${this.paymentPayDetails?.backToCurrentCat}`;
    }
    this.router.navigateByUrl(this.locationService.base_url(backUrl), {state: {clearCart: false}})
    .then(() => {
      window.location.reload();
    });
  }

  private handleStandalonePaymentBack() {
    window.location.href = '';
  }

  protected getPollingConfig(): { intervalMs: number; timeoutMs: number } {
    return {intervalMs: this.fetchInterval, timeoutMs: this.maxTimeout};
  }

  private loadLanguage(locale) {
    return import(`./../../translations/i18n/translation.${locale}.json`)
    .then(lang => {
      this.translate.setTranslation(locale, { ...lang });
      this.translate.setDefaultLang(locale);

      this.langLoaded = true;
      switch (locale) {
        case 'en':
        case 'fr':
        case 'nl':
        case 'el':
        case 'ja':
          this.lang = locale;
          break;
        default:
          this.lang = 'en';
      }

      // store user language to view state...
      this.store.dispatch(new ViewStateUpdateUserLanguage(this.lang, {
        src: 'QrPaymentPayComponent',
        description: 'Load language'
      }));
    });
  }
}
